import { ButtonSm, cn, Icon, ProgressBar } from "@iec-id/ui";
import type { ReactNode } from "react";
import { AuthBrandBar, OfflineNotice } from "@/components/signin/auth-card";
import { SwitchAccountFooter } from "@/components/signin/switch-account";
import type { AuthSearch } from "@/lib/auth-flow";

export interface OnboardingCardProps {
	/** Номер текущего шага, от 1. */
	stepIndex?: number;
	totalSteps?: number;
	stepLabel?: string;
	progressLabel?: string;
	/**
	 * Вернуться на предыдущий шаг. `undefined` — возвращаться некуда (первый
	 * шаг, экраны состояния): кнопка тогда не рисуется вовсе.
	 */
	onBack?: (() => void) | undefined;
	/** Подпись кнопки возврата. Обязательна вместе с `onBack` — умолчаний нет. */
	backLabel?: string;
	/**
	 * `data-testid` счётчика шагов. Нужен браузерным сценариям: подпись
	 * переводится на 21 язык, а роль `status` на карточке уже занята
	 * индикатором офлайна (`tests/e2e/pages/onboarding.page.ts`).
	 */
	progressTestId?: string;
	/**
	 * Контекст входа. Нужен подвалу «войти в другой аккаунт»: человек, ушедший
	 * менять аккаунт, обязан вернуться в тот же сервис-инициатор, а не на
	 * пустую форму без `client_id`.
	 */
	search: AuthSearch;
	children: ReactNode;
	className?: string;
}

/**
 * Экран мастера: шапка со знаком и языком на канве, под ней карточка 420px,
 * r12, паддинг 32, секционный зазор 24 (макет 4b; шкала отступов — в шапке
 * `components/signin/auth-card.tsx`).
 * На 360px паддинг ужимается до 24 — иначе полю остаётся 264px и подпись
 * внутри него начинает переноситься.
 *
 * Шапка со счётчиком и полосой рисуется только когда шаг известен: экран
 * отказа по возрасту живёт в той же карточке, но прогресса у него нет.
 *
 * Подвал со сменой аккаунта — на КАЖДОМ экране мастера, без условий: сессия
 * пережила вкладку, и человек мог начать настройку под чужим аккаунтом, не
 * нажав ни одной кнопки входа.
 */
export function OnboardingCard({
	stepIndex,
	totalSteps,
	stepLabel,
	progressLabel,
	onBack,
	backLabel,
	progressTestId,
	search,
	children,
	className,
}: OnboardingCardProps) {
	const hasProgress = stepIndex !== undefined && totalSteps !== undefined && totalSteps > 0;

	return (
		// Знак и выбор языка — НАД белой плашкой, как на экранах входа
		// (`AuthCard`): мастер идёт сразу за входом и обязан выглядеть тем же
		// продуктом. Внутри карточки шапка читалась как часть формы, а «куда я
		// попал» и «на каком языке» — вопросы к экрану, а не к шагу.
		<div className="flex w-full max-w-[420px] flex-col gap-4">
			{/* Знак IEC ID есть на каждом экране входа. Заодно здесь живёт
			    переключатель языка: человек, промахнувшийся с языком на входе,
			    не должен проходить весь мастер, чтобы его сменить. */}
			<AuthBrandBar />
			<div className={cn("flex flex-col gap-6 rounded-card bg-card p-6 sm:p-8", className)}>
				{/* Мастер — форма из шести шагов, и пропажа связи посреди неё
				    выглядит как «кнопка не работает». Индикатор стоит здесь, а не в
				    каждом шаге: он один на весь мастер, включая экран отказа по
				    возрасту. */}
				<OfflineNotice />
				{/* Возврат и полоса — ОДНА группа навигации (зазор 10), от тела шага
				    её отделяет секционные 24: иначе «Назад» висел одинаково далеко
				    и от полосы, и от заголовка.

				    Возврат стоит НАД шагом, а не под кнопкой действия: внизу он
				    соперничал по весу с «Продолжить» — две кнопки во всю ширину
				    подряд, и вторая читалась как равнозначный исход шага. Наверху
				    это навигация, чем возврат и является. */}
				{onBack || hasProgress ? (
					<div className="flex flex-col gap-2.5">
						{onBack ? (
							// Настоящая кнопка на серой плашке, а не строчная ссылка: возврат
							// — действие шага, и ховер у него обязан быть как у действия
							// (плашка темнеет), а не как у текста. Размер ButtonSm — тот же,
							// что у «Перейти» в задачах витрины.
							<ButtonSm
								className="w-fit"
								iconLeft={<Icon name="ArrowLeft" size={16} />}
								onClick={onBack}
							>
								{backLabel}
							</ButtonSm>
						) : null}
						{hasProgress ? (
							// Номер шага — справа от полосы, на одной с ней линии: строка
							// над полосой освободилась под возврат, а сам счётчик от этого
							// не потерялся — он читается как подпись к полосе.
							<div className="flex items-center gap-3">
								<ProgressBar
									className="min-w-0 flex-1"
									value={Math.round((stepIndex / totalSteps) * 100)}
									label={progressLabel}
								/>
								<span
									{...(progressTestId ? { "data-testid": progressTestId } : {})}
									className="shrink-0 text-caps text-fg-muted"
								>
									{stepLabel}
								</span>
							</div>
						) : null}
					</div>
				) : null}
				{children}
				<SwitchAccountFooter search={search} />
			</div>
		</div>
	);
}
